import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ApiError, apiData } from '@/lib/api';
import { Button, Field, Input } from '@/components/ui';
import { useAuth } from '@/store/auth';
import { AuthLayout } from './AuthLayout';

export function LoginPage() {
  const login = useAuth((s) => s.login);
  const kakaoLogin = useAuth((s) => s.kakaoLogin);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // 카카오 로그인 (§5.1 P5) — 키가 설정된 배포에서만 버튼이 나온다.
  const { data: kakao } = useQuery({
    queryKey: ['auth', 'kakao'],
    queryFn: () => apiData<{ available: boolean; clientId: string | null }>('/auth/kakao'),
    staleTime: Infinity,
    retry: false,
  });

  // 카카오에서 ?code= 로 돌아온 경우 — StrictMode 중복 실행을 ref로 막는다.
  const kakaoCode = searchParams.get('code');
  const exchanging = useRef(false);
  useEffect(() => {
    if (!kakaoCode || exchanging.current) return;
    exchanging.current = true;
    setLoading(true);
    kakaoLogin(kakaoCode, `${window.location.origin}/login`)
      .then(() => navigate('/', { replace: true }))
      .catch((caught) => {
        setSearchParams({}, { replace: true });
        setError(
          caught instanceof ApiError ? caught.message : '카카오 로그인에 실패했어요. 다시 시도해 주세요.',
        );
      })
      .finally(() => {
        exchanging.current = false;
        setLoading(false);
      });
  }, [kakaoCode, kakaoLogin, navigate, setSearchParams]);

  function startKakao() {
    if (!kakao?.clientId) return;
    const redirectUri = `${window.location.origin}/login`;
    // scope를 명시해야 이메일 동의가 요청되고, 이미 연결된 계정에도
    // "추가 동의" 화면이 떠서 이메일 없이 연결된 상태를 복구할 수 있다.
    // (콘솔의 동의항목에 카카오계정(이메일)이 설정되어 있어야 한다 — docs/env.md)
    const url =
      'https://kauth.kakao.com/oauth/authorize' +
      `?client_id=${encodeURIComponent(kakao.clientId)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      '&response_type=code' +
      '&scope=account_email';
    window.location.assign(url);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate('/', { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : '연결이 잠시 불안정해요. 다시 시도해 주세요.',
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      title="다시 만나 반가워요"
      description="이메일과 비밀번호로 들어오실 수 있어요."
      footer={
        <>
          아직 계정이 없으신가요?{' '}
          <Link to="/register" className="font-semibold text-primary-strong underline">
            가입하기
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Field label="이메일" required>
          <Input
            type="email"
            autoComplete="email"
            inputMode="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="parent@example.com"
          />
        </Field>

        <Field label="비밀번호" required>
          <Input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="8자 이상"
          />
        </Field>

        {error && (
          <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
            {error}
          </p>
        )}

        <Button type="submit" block size="lg" loading={loading}>
          로그인
        </Button>

        {kakao?.available && (
          <button
            type="button"
            disabled={loading}
            onClick={startKakao}
            // 카카오 브랜드 버튼 규정 색 — 디자인 토큰 밖의 외부 브랜드 색이라 인라인으로 둔다
            style={{ backgroundColor: '#FEE500', color: '#191919' }}
            className="tap flex h-12 w-full items-center justify-center gap-2 rounded-md text-base font-semibold disabled:opacity-60"
          >
            <KakaoSymbol />
            카카오로 시작하기
          </button>
        )}

        <div className="flex justify-between text-sm">
          <Link to="/forgot-password" className="text-ink-sub underline">
            비밀번호를 잊으셨나요?
          </Link>
          <Link to="/invite" className="text-ink-sub underline">
            초대코드로 시작
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}

/** 카카오 말풍선 심벌 — 공식 심벌 형태의 단색 벡터 */
function KakaoSymbol() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="#191919" aria-hidden>
      <path d="M12 3C6.48 3 2 6.58 2 11c0 2.84 1.87 5.33 4.68 6.74l-.95 3.52c-.08.31.27.56.54.38l4.14-2.75c.52.07 1.05.11 1.59.11 5.52 0 10-3.58 10-8S17.52 3 12 3z" />
    </svg>
  );
}
